import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PromiseStatuses } from "../../../../utils";
import { GetAllGGNotificationCropDTO, GetAllGGNotificationCropFiltersDTO, GGNotificationCropDTO } from "./dataservice/dto";
import { NewGGNotificationCropService } from "./dataservice/service";

interface GGNotificationCropState {
    // REQUEST
    cropCreationRequest: GGNotificationCropDTO

    // FILTERS
    cropFIlters: GetAllGGNotificationCropFiltersDTO

    // STATUS
    cropCreationStatus: PromiseStatuses
    cropDeleteStatus: PromiseStatuses
    getAllCropStatus: PromiseStatuses
    getCropByIdStatus: PromiseStatuses

    // RESPONSE
    getAllCropResponse?: GetAllGGNotificationCropDTO
    getCropByIdResponse?: GGNotificationCropDTO
}

const initialState: GGNotificationCropState = {

    cropCreationRequest: {},

    cropFIlters: {
        itemsPerPage: 0,
        page: 0
    },

    cropCreationStatus: 'idle',
    cropDeleteStatus: 'idle',
    getAllCropStatus: 'idle',
    getCropByIdStatus: 'idle'
}

export const GetAllGGNotificationCrop = createAsyncThunk(
    'GGNotificationCrop/GetAll',
    async (filters: GetAllGGNotificationCropFiltersDTO, thunkApi): Promise<GetAllGGNotificationCropDTO> => {
        const GGNotificationCropService = NewGGNotificationCropService()
        return GGNotificationCropService.GetAllGGNotificationCrop(filters).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetGGNotificationCropById = createAsyncThunk(
    'GGNotificationCrop/GetById',
    async (id: string, thunkApi): Promise<GGNotificationCropDTO> => {
        const GGNotificationCropService = NewGGNotificationCropService()
        return GGNotificationCropService.GetGGNotificationCropById(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GGNotificationCropCreation = createAsyncThunk(
    'GGNotificationCrop/Creation',
    async (data: GGNotificationCropDTO, thunkApi): Promise<void> => {
        const GGNotificationCropService = NewGGNotificationCropService()
        return GGNotificationCropService.CreateGGNotificationCrop(data).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GGNotificationCropDelete = createAsyncThunk(
    'GGNotificationCrop/Delete',
    async (id: string, thunkApi): Promise<void> => {
        const GGNotificationCropService = NewGGNotificationCropService()
        return GGNotificationCropService.DeleteGGNotificationCrop(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

const GGNotificationCropSlice = createSlice({
    name: 'ggNotificationCrop/slice',
    initialState,
    reducers: {
        // REQUEST
        setCropCreationGGNotificationId: (state, action) => {
            state.cropCreationRequest.ggNotificationId = action.payload
        },
        setCropCreationDescription: (state, action) => {
            state.cropCreationRequest.description = action.payload
        },
        setCropCreationAddress: (state, action) => {
            state.cropCreationRequest.address = action.payload
        },
        setCropCreationLatitude: (state, action) => {
            state.cropCreationRequest.latitude = action.payload
        },
        setCropCreationLongitude: (state, action) => {
            state.cropCreationRequest.longitude = action.payload
        },
        setCropCreationProduct: (state, action) => {
            state.cropCreationRequest.product = action.payload
        },
        setCropCreationArea: (state, action) => {
            state.cropCreationRequest.area = action.payload
        },
        setCropCreationHarvestingPeriod: (state, action) => {
            state.cropCreationRequest.harvestingPeriod = action.payload
        },
        setCropCreationHarvestingExclusion: (state, action) => {
            state.cropCreationRequest.harvestingExclusion = action.payload
        },
        setCropCreationCropType: (state, action) => {
            state.cropCreationRequest.cropType = action.payload
        },
        setCropCreationHarvestingType: (state, action) => {
            state.cropCreationRequest.harvestingType = action.payload
        },
        setCropCreationManipulation: (state, action) => {
            state.cropCreationRequest.manipulation = action.payload
        },
        setCropCreationManipulationType: (state, action) => {
            state.cropCreationRequest.manipulationType = action.payload
        },
        setCropCreationParallelProperty: (state, action) => {
            state.cropCreationRequest.parallelProperty = action.payload
        },
        setCropCreationSubcontractedManipulation: (state, action) => {
            state.cropCreationRequest.subcontractedManipulation = action.payload
        },
        setCropCreationOtherSubcontractedActivity: (state, action) => {
            state.cropCreationRequest.otherSubcontractedActivity = action.payload
        },
        setCropCreationDestinationCountry: (state, action) => {
            state.cropCreationRequest.destinationCountry = action.payload
        },
        resetCropCreationRequest: (state) => {
            state.cropCreationRequest = {}
        },

        // STATUS
        resetCropCreationStatus: (state) => {
            state.cropCreationStatus = 'idle'
        },
        resetGetAllCropStatus: (state) => {
            state.getAllCropStatus = 'idle'
        },
        resetGetCropByIdStatus: (state) => {
            state.getCropByIdStatus = 'idle'
        },
        resetCropDeleteStatus: (state) => {
            state.cropDeleteStatus = 'idle'
        }
    },
    extraReducers(builder) {
        builder

            // GET ALL
            .addCase(GetAllGGNotificationCrop.pending, (state) => {
                state.getAllCropStatus = 'loading'
            })
            .addCase(GetAllGGNotificationCrop.fulfilled, (state, action) => {
                state.getAllCropStatus = 'successfully'
                state.getAllCropResponse = action.payload
            })
            .addCase(GetAllGGNotificationCrop.rejected, (state, action) => {
                state.getAllCropStatus = 'failed'
            })

            // GET BY ID
            .addCase(GetGGNotificationCropById.pending, (state) => {
                state.getCropByIdStatus = 'loading'
            })
            .addCase(GetGGNotificationCropById.fulfilled, (state, action) => {
                state.getCropByIdStatus = 'successfully'
                state.getCropByIdResponse = action.payload
            })
            .addCase(GetGGNotificationCropById.rejected, (state, action) => {
                state.getCropByIdStatus = 'failed'
            })

            // CREATION
            .addCase(GGNotificationCropCreation.pending, (state) => {
                state.cropCreationStatus = 'loading'
            })
            .addCase(GGNotificationCropCreation.fulfilled, (state) => {
                state.cropCreationStatus = 'successfully'
            })
            .addCase(GGNotificationCropCreation.rejected, (state, action) => {
                state.cropCreationStatus = 'failed'
            })

            // DELETE
            .addCase(GGNotificationCropDelete.pending, (state) => {
                state.cropDeleteStatus = 'loading'
            })
            .addCase(GGNotificationCropDelete.fulfilled, (state) => {
                state.cropDeleteStatus = 'successfully'
            })
            .addCase(GGNotificationCropDelete.rejected, (state, action) => {
                state.cropDeleteStatus = 'failed'
            })
    },
})

export const {
    setCropCreationAddress,
    setCropCreationArea,
    setCropCreationCropType,
    setCropCreationDescription,
    setCropCreationDestinationCountry,
    setCropCreationGGNotificationId,
    setCropCreationHarvestingExclusion,
    setCropCreationHarvestingPeriod,
    setCropCreationHarvestingType,
    setCropCreationLatitude,
    setCropCreationLongitude,
    setCropCreationManipulation,
    setCropCreationManipulationType,
    setCropCreationOtherSubcontractedActivity,
    setCropCreationParallelProperty,
    setCropCreationProduct,
    setCropCreationSubcontractedManipulation,
    resetCropCreationRequest,
    resetCropCreationStatus,
    resetGetAllCropStatus,
    resetGetCropByIdStatus,
    resetCropDeleteStatus
} = GGNotificationCropSlice.actions

export default GGNotificationCropSlice.reducer